import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import PayPalFnfButton from '../components/PayPalFnfButton';
import axios from 'axios';
import { toast } from 'sonner';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Button } from '../components/ui/button';
import { Checkbox } from '../components/ui/checkbox';
import { 
  ShoppingCart, User, LogOut, Shield, ArrowLeft, AlertTriangle, Copy, Home, Zap, Truck, XCircle, DollarSign, Tag, Check, Loader2, CreditCard, Package
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import { validateAddress } from '../components/AddressValidationBanner';
import { errMsg } from '../lib/utils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_91be65d6-9fee-4eda-abde-3fd83cab62ae/artifacts/kxr3j7eu_IMG_7793.png";

export default function Checkout() {
  const navigate = useNavigate();
  const { user, userProfile, logout, getAuthHeader, refreshProfile } = useAuth();
  const { cart, itemCount, fetchCart, clearCart } = useCart();
  
  const [formData, setFormData] = useState({
    shipping_name: '',
    shipping_address: '',
    shipping_apt_unit: '',
    shipping_city: '',
    shipping_state: '',
    shipping_zip: '',
    priority_shipping: false,
    overnight_shipping: false,
    notes: ''
  });
  const [saveShipping, setSaveShipping] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [fbconMode, setFbconMode] = useState(false);
  const [orderPlaced, setOrderPlaced] = useState(false);
  const [orderId, setOrderId] = useState(null);
  const [orderHasTrade, setOrderHasTrade] = useState(false);
  const [orderTotal, setOrderTotal] = useState(0);
  
  // Promo code state
  const [promoCode, setPromoCode] = useState('');
  const [validatingCode, setValidatingCode] = useState(false);
  const [appliedCode, setAppliedCode] = useState(null); // { code, type, discount_amount, message }
  const [codeError, setCodeError] = useState(null);
  
  // Gift card state
  const [giftCardCode, setGiftCardCode] = useState('');
  const [validatingGiftCard, setValidatingGiftCard] = useState(false);
  const [appliedGiftCard, setAppliedGiftCard] = useState(null); // { code, balance, applicable_amount }
  const [giftCardError, setGiftCardError] = useState(null);
  
  // Ref for auto-scrolling to Place Order button
  const placeOrderRef = useRef(null);

  // Pre-fill from user profile
  useEffect(() => {
    console.log('userProfile:', userProfile);
    if (userProfile) {
      setFormData(prev => ({
        ...prev,
        shipping_name: userProfile.full_name || '',
        shipping_address: userProfile.address || '',
        shipping_apt_unit: userProfile.apt_unit || '',
        shipping_city: userProfile.city || '',
        shipping_state: userProfile.state || '',
        shipping_zip: userProfile.zip_code || ''
      }));
    }
  }, [userProfile]);

  // Check FBCON mode
  useEffect(() => {
    const checkFbcon = async () => {
      try {
        const res = await axios.get(`${API}/drop/status`);
        if (res.data?.fbcon_mode) setFbconMode(true);
      } catch (e) {}
    };
    checkFbcon();
  }, []);

  
  // Auto-apply BLKCRD discount for VIP users (only if cart has eligible items)
  useEffect(() => {
    if (userProfile?.has_blkcrd && cart.items.length > 0) {
      // Check if cart has any offer items (discount codes not allowed with offers)
      const hasOffers = cart.items.some(item => item.offer_price !== undefined && item.offer_price !== null);
      // Check if cart has any eligible items (not gift cards, BLKCRD, auctions, or trade-ins)
      const hasEligibleItems = cart.items.some(item => !item.is_gift_card && !item.is_blkcrd && !item.is_bidding && !item.is_trade);
      if (!hasOffers && !appliedCode && hasEligibleItems) {
        setAppliedCode({
          code: 'BLKCRD',
          type: 'blkcrd',
          discount_percent: 15,
          message: 'BLKCRD - 15% OFF',
          is_auto: true
        });
      }
    }
  }, [userProfile, cart.items, appliedCode]);

  // Refresh cart on mount to get latest availability
  useEffect(() => {
    fetchCart();
  }, []);

  // Auto-scroll to Place Order button after cart loads
  useEffect(() => {
    if (cart.items.length > 0 && placeOrderRef.current && !orderPlaced) {
      const timer = setTimeout(() => {
        placeOrderRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 150);
      return () => clearTimeout(timer);
    }
  }, [cart.items.length, orderPlaced]);

  const handleChange = (e) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  // Check if cart has any offer items (discount codes not allowed)
  const hasOfferItems = cart.items.some(item => item.offer_price !== undefined && item.offer_price !== null);
  
  // Check if cart only has digital items (gift cards and/or BLKCRD) - no shipping needed
  const isDigitalOnly = cart.items.length > 0 && cart.items.every(item => item.is_gift_card || item.is_blkcrd);
  
  // Force shipping upgrades off for digital-only orders.
  // Overnight takes precedence over priority (mutually exclusive in the UI).
  const effectiveOvernightShipping = isDigitalOnly ? false : formData.overnight_shipping;
  const effectivePriorityShipping = isDigitalOnly ? false : (formData.priority_shipping && !effectiveOvernightShipping);
  const shippingCost = effectiveOvernightShipping ? 50.00 : (effectivePriorityShipping ? 15.00 : 0.00);
  
  // Calculate eligible subtotal (excludes gift cards, BLKCRD items, auctions, and trade-ins from discounts)
  const eligibleSubtotal = cart.items.reduce((sum, item) => {
    if (item.is_gift_card || item.is_blkcrd || item.is_bidding || item.is_trade) return sum;
    return sum + (item.price * (item.quantity || 1));
  }, 0);
  
  // Calculate discount - handle BLKCRD percentage or fixed amount from code validation
  // Discounts only apply to eligible items (not gift cards, BLKCRD, or auctions)
  const calculateDiscount = () => {
    if (!appliedCode) return 0;
    if (appliedCode.discount_percent) {
      // For percentage discounts (like BLKCRD), only discount eligible items
      return eligibleSubtotal * (appliedCode.discount_percent / 100);
    }
    // For fixed amount discounts, cap at eligible subtotal
    return Math.min(appliedCode.discount_amount || 0, eligibleSubtotal);
  };
  const discountAmount = calculateDiscount();
  const subtotal = cart.subtotal || cart.total || 0;
  const totalBeforeGiftCard = subtotal - discountAmount + shippingCost;
  
  // Gift card amount (stacks with other discounts)
  const giftCardAmount = appliedGiftCard ? Math.min(appliedGiftCard.applicable_amount, totalBeforeGiftCard) : 0;
  const total = totalBeforeGiftCard - giftCardAmount;

  // Validate promo/referral code
  const validatePromoCode = async () => {
    if (!promoCode.trim()) return;
    
    setValidatingCode(true);
    setCodeError(null);
    
    try {
      const response = await axios.post(
        `${API}/validate-code`,
        {
          code: promoCode.trim(),
          order_total: cart.total,
          has_offer_items: hasOfferItems
        },
        { headers: getAuthHeader() }
      );
      
      if (response.data.valid) {
        setAppliedCode({
          code: response.data.code,
          type: response.data.code_type,
          discount_amount: response.data.discount_amount,
          message: response.data.message
        });
        toast.success(response.data.message);
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'INVALID CODE';
      setCodeError(errorMsg);
      setAppliedCode(null);
      toast.error(errorMsg);
    } finally {
      setValidatingCode(false);
    }
  };

  // Validate gift card
  const validateGiftCard = async () => {
    if (!giftCardCode.trim()) return;
    
    setValidatingGiftCard(true);
    setGiftCardError(null);
    
    try {
      const response = await axios.post(
        `${API}/gift-cards/validate-for-checkout`,
        { code: giftCardCode.trim() },
        { 
          headers: getAuthHeader(),
          params: { order_total: totalBeforeGiftCard }
        }
      );
      
      if (response.data.valid) {
        setAppliedGiftCard({
          code: response.data.code,
          balance: response.data.current_balance,
          applicable_amount: response.data.applicable_amount,
          remaining_after_use: response.data.remaining_after_use
        });
        toast.success(response.data.message);
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'INVALID GIFT CARD';
      setGiftCardError(errorMsg);
      setAppliedGiftCard(null);
      toast.error(errorMsg);
    } finally {
      setValidatingGiftCard(false);
    }
  };

  // Remove applied gift card
  const removeGiftCard = () => {
    setAppliedGiftCard(null);
    setGiftCardCode('');
    setGiftCardError(null);
  };

  // Remove applied code
  const removeAppliedCode = () => {
    setAppliedCode(null);
    setPromoCode('');
    setCodeError(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.shipping_name || !formData.shipping_address || !formData.shipping_city || 
        !formData.shipping_state || !formData.shipping_zip) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      setSubmitting(true);
      
      // Save shipping info if checkbox checked
      if (saveShipping) {
        try {
          await axios.put(
            `${API}/auth/update-shipping`,
            {
              full_name: formData.shipping_name,
              address: formData.shipping_address,
              apt_unit: formData.shipping_apt_unit,
              city: formData.shipping_city,
              state: formData.shipping_state,
              zip_code: formData.shipping_zip
            },
            { headers: getAuthHeader() }
          );
          // Refresh profile to update saved shipping
          await refreshProfile();
          toast.success('Shipping info saved');
        } catch (err) {
          console.log('Could not save shipping info:', err);
        }
      }
      
      const response = await axios.post(
        `${API}/orders`,
        {
          ...formData,
          promo_code: appliedCode?.code || null,
          gift_card_code: appliedGiftCard?.code || null
        },
        { headers: getAuthHeader() }
      );
      setOrderId(response.data.id);
      setOrderTotal(response.data.total);
      // Capture trade flag before clearing cart so success view can show shipping address
      setOrderHasTrade((response.data.items || cart.items || []).some(it => it.is_trade));
      setOrderPlaced(true);
      // Clear cart immediately after successful order
      await clearCart();
      toast.success('ORDER PLACED - PENDING PAYMENT');
    } catch (error) {
      toast.error(errMsg(error, 'Failed to place order'));
      // Refresh cart in case items were taken
      fetchCart();
    } finally {
      setSubmitting(false);
    }
  };

  const copyOrderId = () => {
    navigator.clipboard.writeText(orderId);
    toast.success('Order ID copied');
  };

  if (cart.items.length === 0 && !orderPlaced) {
    navigate('/cart');
    return null;
  }

  // Validate user's saved address before allowing checkout
  const addressValidation = validateAddress(userProfile);
  const addressBlocked = !addressValidation.valid;

  // Show address block if validation fails
  if (addressBlocked && !orderPlaced) {
    return (
      <div className="min-h-screen bg-[#050505]">
        {/* Header */}
        <header className="fixed left-0 right-0 z-50 border-b border-zinc-800 bg-[#050505]/90 backdrop-blur-sm" style={{ top: 'var(--banner-offset, 0px)' }}>
          <div className="max-w-7xl mx-auto px-4 md:px-8 h-16 flex items-center justify-between">
            <Link to="/catalog" className="flex items-center gap-3">
              <img src={LOGO_URL} alt="BLK MRKT" className="h-8 w-auto" />
            </Link>
            <nav className="flex items-center gap-4">
              <Link to="/catalog" className="p-2 text-zinc-400 hover:text-tiffany-500 transition-colors">
                <Home className="w-5 h-5" />
              </Link>
              <Link to="/cart" className="relative p-2 text-zinc-400 hover:text-tiffany-500 transition-colors">
                <ShoppingCart className="w-5 h-5" />
                {itemCount > 0 && (
                  <span className="absolute -top-1 -right-1 bg-tiffany-500 text-black text-xs font-bold w-5 h-5 flex items-center justify-center">
                    {itemCount}
                  </span>
                )}
              </Link>
              <Link to="/dashboard" className="p-2 text-zinc-400 hover:text-tiffany-500 transition-colors">
                <User className="w-5 h-5" />
              </Link>
              <button onClick={logout} className="p-2 text-zinc-400 hover:text-red-500 transition-colors">
                <LogOut className="w-5 h-5" />
              </button>
            </nav>
          </div>
        </header>

        <main className="pt-24 pb-12 px-4 md:px-8 max-w-2xl mx-auto">
          <div className={`border p-8 ${addressValidation.isInternational ? 'border-yellow-500 bg-yellow-500/5' : 'border-orange-500 bg-orange-500/5'}`}>
            <div className="flex items-center gap-3 mb-6">
              <AlertTriangle className={`w-8 h-8 ${addressValidation.isInternational ? 'text-yellow-500' : 'text-orange-500'}`} />
              <h1 className="font-sans text-2xl text-white">
                {addressValidation.isInternational ? 'FORWARDING ADDRESS REQUIRED' : 'SHIPPING ADDRESS REQUIRED'}
              </h1>
            </div>
            
            {addressValidation.isInternational ? (
              <div className="space-y-4 mb-8">
                <p className="font-mono text-sm text-zinc-300">
                  As an international customer, you must set up a <span className="text-yellow-500 font-bold">US forwarding address</span> before checkout.
                </p>
                <div className="bg-zinc-900 border border-zinc-800 p-4">
                  <p className="font-mono text-xs text-zinc-400 mb-2">RECOMMENDED FORWARDING SERVICES:</p>
                  <ul className="font-mono text-sm text-zinc-300 space-y-1">
                    <li>• <a href="https://www.shipito.com" target="_blank" rel="noopener noreferrer" className="text-tiffany-500 underline">Shipito</a></li>
                    <li>• <a href="https://www.myus.com" target="_blank" rel="noopener noreferrer" className="text-tiffany-500 underline">MyUS</a></li>
                    <li>• <a href="https://www.stackry.com" target="_blank" rel="noopener noreferrer" className="text-tiffany-500 underline">Stackry</a></li>
                  </ul>
                </div>
                <p className="font-mono text-xs text-zinc-500">
                  Your forwarding service will give you a US address with a unique suite number. Enter that address in your profile.
                </p>
              </div>
            ) : (
              <div className="mb-8">
                <p className="font-mono text-sm text-zinc-300">
                  Please complete your shipping address to continue with checkout.
                </p>
              </div>
            )}

            <div className="bg-zinc-900 border border-zinc-800 p-4 mb-8">
              <p className="font-mono text-xs text-zinc-500 mb-2">ISSUES TO FIX:</p>
              <ul className="space-y-1">
                {addressValidation.issues.map((issue, idx) => (
                  <li key={idx} className="font-mono text-sm text-red-400 flex items-start gap-2">
                    <XCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                    {issue}
                  </li>
                ))}
              </ul>
            </div>

            <div className="flex flex-col sm:flex-row gap-3">
              <Link 
                to="/dashboard?edit=address" 
                className="flex-1 bg-tiffany-500 text-black font-mono text-sm py-3 px-6 hover:bg-tiffany-400 transition-colors flex items-center justify-center gap-2"
              >
                UPDATE MY ADDRESS
              </Link>
              <Link 
                to="/cart" 
                className="flex-1 border border-zinc-700 text-zinc-300 font-mono text-sm py-3 px-6 hover:border-zinc-500 transition-colors flex items-center justify-center gap-2"
              >
                <ArrowLeft className="w-4 h-4" />
                BACK TO CART
              </Link>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505]">
      {/* Header */}
      <header className="fixed left-0 right-0 z-50 border-b border-zinc-800 bg-[#050505]/90 backdrop-blur-sm" style={{ top: 'var(--banner-offset, 0px)' }}>
        <div className="max-w-7xl mx-auto px-4 md:px-8 h-16 flex items-center justify-between">
          <Link to="/catalog" className="flex items-center gap-3">
            <img src={LOGO_URL} alt="BLK MRKT" className="h-8 w-auto" />
          </Link>
          
          <nav className="flex items-center gap-4">
            <Link 
              to="/catalog"
              className="p-2 text-zinc-400 hover:text-tiffany-500 transition-colors"
              data-testid="home-link"
            >
              <Home className="w-5 h-5" />
            </Link>
            <Link 
              to="/cart" 
              className="relative p-2 text-zinc-400 hover:text-tiffany-500 transition-colors"
              data-testid="cart-link"
            >
              <ShoppingCart className="w-5 h-5" />
              {itemCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-tiffany-500 text-black text-xs font-bold w-5 h-5 flex items-center justify-center">
                  {itemCount}
                </span>
              )}
            </Link>
            <Link 
              to="/dashboard" 
              className="p-2 text-zinc-400 hover:text-tiffany-500 transition-colors"
              data-testid="dashboard-link"
            >
              <User className="w-5 h-5" />
            </Link>
            {user?.is_admin && (
              <Link 
                to="/admin" 
                className="p-2 text-zinc-400 hover:text-tiffany-500 transition-colors"
                data-testid="admin-link"
              >
                <Shield className="w-5 h-5" />
              </Link>
            )}
            <button 
              onClick={logout}
              className="p-2 text-zinc-400 hover:text-red-500 transition-colors"
              data-testid="logout-btn"
            >
              <LogOut className="w-5 h-5" />
            </button>
          </nav>
        </div>
      </header>

      <main className="pt-20 sm:pt-24 pb-12 px-4 md:px-8 max-w-4xl mx-auto">
        {orderPlaced ? (
          /* Order Confirmation */
          (() => {
            const needsPayment = orderTotal > 0;
            return (
          <div className="text-center py-6">
            
            {needsPayment ? (
              <>
                {/* HERO: Pay button is THE first thing the user sees */}
                <div className="max-w-md mx-auto mb-6">
                  <p className="font-mono text-[10px] text-zinc-500 tracking-[3px] mb-1.5">TOTAL DUE</p>
                  <p className="font-mono text-5xl text-white font-bold mb-5">${orderTotal.toFixed(2)}</p>
                  
                  <PayPalFnfButton amount={orderTotal} />
                  
                  <p className="font-mono text-[11px] text-zinc-500 text-center mt-2.5 leading-relaxed">
                    Send as <span className="text-white font-bold">"FRIENDS &amp; FAMILY"</span> &middot; G&amp;S will be refunded
                  </p>
                </div>

                {/* Compact order id row */}
                <div className="max-w-md mx-auto mb-4 flex items-center justify-center gap-2 text-zinc-500">
                  <span className="font-mono text-xs">ORDER</span>
                  <code className="font-mono text-xs text-tiffany-500" data-testid="order-id">
                    {orderId}
                  </code>
                  <button
                    onClick={copyOrderId}
                    className="text-zinc-600 hover:text-tiffany-500"
                    data-testid="copy-order-id"
                    title="Copy order ID"
                  >
                    <Copy className="w-3 h-3" />
                  </button>
                  <span className="text-zinc-700 mx-1">&middot;</span>
                  <span className="font-mono text-[11px] text-zinc-500">expires in 30 min</span>
                </div>
              </>
            ) : (
              <>
                <h1 className="font-sans text-3xl sm:text-4xl text-white mb-2" data-testid="order-confirmed">
                  ORDER PLACED
                </h1>
                <p className="font-mono text-zinc-400 text-sm mb-6 max-w-md mx-auto">
                  {orderHasTrade
                    ? 'Ship your trade-in to lock in your deal'
                    : 'You\'re all set — we\'ll be in touch soon'}
                </p>
                <div className="border border-zinc-800 p-4 max-w-md mx-auto mb-6">
                  <p className="font-mono text-zinc-500 text-xs mb-1">ORDER ID</p>
                  <div className="flex items-center justify-center gap-2">
                    <code className="font-mono text-tiffany-500 text-xl sm:text-2xl font-bold" data-testid="order-id-zero">
                      {orderId}
                    </code>
                    <button
                      onClick={copyOrderId}
                      className="p-2 text-zinc-500 hover:text-tiffany-500 transition-colors"
                      data-testid="copy-order-id-zero"
                    >
                      <Copy className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </>
            )}

            {/* Send-manually accordion (only for paid orders) */}
            {needsPayment && (
              <details className="max-w-md mx-auto mb-6 border border-zinc-800 bg-zinc-900/40">
                <summary className="cursor-pointer list-none px-3 py-2.5 flex items-center justify-between text-zinc-500 hover:text-white">
                  <span className="font-mono text-[11px] tracking-widest">SEND MANUALLY</span>
                  <span className="font-mono text-xs">+</span>
                </summary>
                <div className="px-3 pb-3 pt-1 space-y-2 text-left">
                  <div className="flex items-center justify-between gap-2">
                    <div className="min-w-0">
                      <p className="font-mono text-[10px] text-zinc-500">PAYPAL F&amp;F TO</p>
                      <p className="font-mono text-sm text-white truncate">payments@shopblkmrkt.com</p>
                    </div>
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText('payments@shopblkmrkt.com');
                        toast.success('Email copied');
                      }}
                      className="p-2 text-zinc-500 hover:text-tiffany-500 flex-shrink-0"
                    >
                      <Copy className="w-4 h-4" />
                    </button>
                  </div>
                  <div>
                    <p className="font-mono text-[10px] text-zinc-500">OR USERNAME</p>
                    <p className="font-mono text-sm text-white">@BLKMRKTFB <span className="text-zinc-500 text-[11px]">(Ryan Grenier)</span></p>
                  </div>
                  <p className="font-mono text-[10px] text-zinc-500 pt-2 border-t border-zinc-800">
                    Include order ID <span className="text-tiffany-500">{orderId}</span> in the payment note.
                  </p>
                </div>
              </details>
            )}

            {/* Trade Ship-To Address — only for orders containing trades */}
            {orderHasTrade && (
              <div className="border border-tiffany-500/40 bg-tiffany-500/5 p-4 max-w-md mx-auto mb-6 text-left" data-testid="trade-shipping-address">
                <div className="flex items-center gap-2 mb-3">
                  <Package className="w-4 h-4 text-tiffany-500 flex-shrink-0" />
                  <h3 className="font-mono text-xs text-tiffany-500 font-bold tracking-widest">SHIP TRADE-IN TO</h3>
                </div>
                <div className="flex items-start justify-between gap-3">
                  <div className="font-mono text-white text-sm leading-relaxed">
                    <p className="text-tiffany-500 font-bold">BLK MRKT</p>
                    <p>PO BOX 26</p>
                    <p>SACO, ME 04072</p>
                  </div>
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText('BLK MRKT\nPO BOX 26\nSACO, ME 04072');
                      toast.success('Address copied!');
                    }}
                    className="p-2 text-zinc-400 hover:text-tiffany-500 hover:bg-tiffany-500/10 transition-colors flex-shrink-0"
                    data-testid="copy-trade-address-btn"
                    title="Copy address"
                  >
                    <Copy className="w-4 h-4" />
                  </button>
                </div>
                <p className="font-mono text-[10px] text-zinc-500 mt-3">
                  Write order ID <span className="text-tiffany-500">{orderId}</span> on or inside the package.
                </p>
              </div>
            )}

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                onClick={() => navigate('/dashboard')}
                className="bg-tiffany-500 text-black hover:bg-tiffany-400 font-sans tracking-widest rounded-none"
                data-testid="view-orders-btn"
              >
                VIEW MY ORDERS
              </Button>
              <Button
                onClick={() => navigate('/catalog')}
                variant="outline"
                className="border-zinc-800 text-zinc-300 hover:bg-zinc-800 font-sans tracking-widest rounded-none"
                data-testid="continue-shopping-btn"
              >
                CONTINUE SHOPPING
              </Button>
            </div>
          </div>
            );
          })()
        ) : (
          /* Checkout Form - Mobile Optimized */
          <>
            <div className="flex items-center justify-between mb-4">
              <Link 
                to="/cart"
                className="inline-flex items-center gap-1 text-zinc-500 hover:text-tiffany-500 font-mono text-xs transition-colors"
                data-testid="back-to-cart"
              >
                <ArrowLeft className="w-3 h-3" />
                CART
              </Link>
              <h1 className="font-sans text-2xl text-white tracking-tighter" data-testid="checkout-title">
                CHECKOUT
              </h1>
              <div className="w-12"></div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Shipping Form */}
              <div className="border border-zinc-800 p-4">
                <div className="flex items-center justify-between mb-3">
                  <span className="font-mono text-xs text-zinc-500">SHIP TO</span>
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="save-shipping"
                      checked={saveShipping}
                      onCheckedChange={setSaveShipping}
                      className="w-4 h-4 border-zinc-600 data-[state=checked]:bg-tiffany-500 data-[state=checked]:border-tiffany-500 rounded-none"
                      data-testid="save-shipping-checkbox"
                    />
                    <label htmlFor="save-shipping" className="font-mono text-xs text-zinc-500 cursor-pointer">
                      SAVE ADDRESS
                    </label>
                  </div>
                </div>
                
                <div className="space-y-3">
                  <Input
                    name="shipping_name"
                    value={formData.shipping_name}
                    onChange={handleChange}
                    placeholder="Full Name"
                    autoComplete="name"
                    className="h-11 bg-zinc-900 border-zinc-700 text-white font-mono placeholder:text-zinc-600 focus:border-tiffany-500 rounded-none"
                    required
                    data-testid="shipping-name"
                  />
                  <Input
                    name="shipping_address"
                    value={formData.shipping_address}
                    onChange={handleChange}
                    placeholder="Street Address"
                    autoComplete="street-address"
                    className="h-11 bg-zinc-900 border-zinc-700 text-white font-mono placeholder:text-zinc-600 focus:border-tiffany-500 rounded-none"
                    required
                    data-testid="shipping-address"
                  />
                  <Input
                    name="shipping_apt_unit"
                    value={formData.shipping_apt_unit}
                    onChange={handleChange}
                    placeholder="Apt / Unit / Suite (Optional)"
                    autoComplete="address-line2"
                    className="h-11 bg-zinc-900 border-zinc-700 text-white font-mono placeholder:text-zinc-600 focus:border-tiffany-500 rounded-none"
                    data-testid="shipping-apt-unit"
                  />
                  <div className="grid grid-cols-3 gap-2">
                    <Input
                      name="shipping_city"
                      value={formData.shipping_city}
                      onChange={handleChange}
                      placeholder="City"
                      autoComplete="address-level2"
                      className="h-11 bg-zinc-900 border-zinc-700 text-white font-mono placeholder:text-zinc-600 focus:border-tiffany-500 rounded-none"
                      required
                      data-testid="shipping-city"
                    />
                    <Input
                      name="shipping_state"
                      value={formData.shipping_state}
                      onChange={handleChange}
                      placeholder="State"
                      autoComplete="address-level1"
                      className="h-11 bg-zinc-900 border-zinc-700 text-white font-mono placeholder:text-zinc-600 focus:border-tiffany-500 rounded-none"
                      required
                      data-testid="shipping-state"
                    />
                    <Input
                      name="shipping_zip"
                      value={formData.shipping_zip}
                      onChange={handleChange}
                      placeholder="ZIP"
                      autoComplete="postal-code"
                      className="h-11 bg-zinc-900 border-zinc-700 text-white font-mono placeholder:text-zinc-600 focus:border-tiffany-500 rounded-none"
                      required
                      data-testid="shipping-zip"
                    />
                  </div>
                </div>
              </div>

              {/* Order Notes - Always visible */}
              <div className="border border-zinc-800 p-4">
                <span className="font-mono text-xs text-zinc-500 block mb-2">ORDER NOTES (OPTIONAL)</span>
                <Textarea
                  name="notes"
                  value={formData.notes}
                  onChange={handleChange}
                  placeholder="Any special instructions..."
                  className="w-full min-h-[60px] bg-zinc-900 border-zinc-700 text-white font-mono placeholder:text-zinc-600 focus:border-tiffany-500 rounded-none resize-none"
                  data-testid="order-notes"
                />
              </div>

              {/* Order Summary + Priority + Place Order - All together */}
              <div ref={placeOrderRef} className="border-2 border-tiffany-500 bg-zinc-900 p-4 space-y-4">
                {/* Items Summary */}
                <div>
                  <span className="font-mono text-xs text-zinc-500 block mb-2">YOUR ORDER</span>
                  <div className="space-y-2">
                    {cart.items.map((item) => (
                      <div key={item.product_id} className="flex justify-between items-start">
                        <div className="flex-1 min-w-0">
                          {item.brand && (
                            <span className="font-mono text-[10px] text-tiffany-500 uppercase block">
                              {item.brand}
                            </span>
                          )}
                          <span className="font-mono text-sm text-white block uppercase" data-testid={`checkout-item-name-${item.product_id}`}>
                            {item.name}
                            {item.variant_name && <span className="text-tiffany-500"> ({item.variant_name})</span>}
                          </span>
                          <span className="font-mono text-xs text-zinc-500">QTY: {item.quantity}</span>
                        </div>
                        <span className="font-mono text-sm text-white ml-2">${item.item_total.toFixed(2)}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Priority Shipping Option - Hidden for digital-only orders and FBCON mode */}
                {!isDigitalOnly && !fbconMode && (
                <div 
                  className={`p-3 border cursor-pointer transition-all ${
                    formData.priority_shipping 
                      ? 'border-tiffany-500 bg-tiffany-500/10' 
                      : 'border-zinc-700 hover:border-zinc-600'
                  }`}
                  onClick={() => setFormData(prev => ({
                    ...prev,
                    priority_shipping: !prev.priority_shipping,
                    // Mutually exclusive with overnight — selecting priority
                    // clears overnight so totals never double-count.
                    overnight_shipping: !prev.priority_shipping ? false : prev.overnight_shipping,
                  }))}
                  data-testid="priority-shipping-toggle"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Zap className={`w-4 h-4 ${formData.priority_shipping ? 'text-tiffany-500' : 'text-zinc-500'}`} />
                      <div>
                        <span className="font-mono text-sm text-white">PRIORITY SHIPPING</span>
                        <span className="font-mono text-xs text-zinc-500 block">2-3 day delivery</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-sm text-tiffany-500">+$15</span>
                      <div className={`w-5 h-5 border flex items-center justify-center ${
                        formData.priority_shipping 
                          ? 'border-tiffany-500 bg-tiffany-500' 
                          : 'border-zinc-600'
                      }`}>
                        {formData.priority_shipping && <Truck className="w-3 h-3 text-black" />}
                      </div>
                    </div>
                  </div>
                </div>
                )}

                {/* Overnight Shipping Option — same UX as priority, $25, mutually
                    exclusive with priority. Hidden for digital-only / FBCON. */}
                {!isDigitalOnly && !fbconMode && (
                <div
                  className={`p-3 border cursor-pointer transition-all ${
                    formData.overnight_shipping
                      ? 'border-tiffany-500 bg-tiffany-500/10'
                      : 'border-zinc-700 hover:border-zinc-600'
                  }`}
                  onClick={() => setFormData(prev => ({
                    ...prev,
                    overnight_shipping: !prev.overnight_shipping,
                    priority_shipping: !prev.overnight_shipping ? false : prev.priority_shipping,
                  }))}
                  data-testid="overnight-shipping-toggle"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Zap className={`w-4 h-4 ${formData.overnight_shipping ? 'text-tiffany-500' : 'text-zinc-500'}`} />
                      <div>
                        <span className="font-mono text-sm text-white">OVERNIGHT SHIPPING</span>
                        <span className="font-mono text-xs text-zinc-500 block">Next-business-day delivery</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-sm text-tiffany-500">+$50</span>
                      <div className={`w-5 h-5 border flex items-center justify-center ${
                        formData.overnight_shipping
                          ? 'border-tiffany-500 bg-tiffany-500'
                          : 'border-zinc-600'
                      }`}>
                        {formData.overnight_shipping && <Truck className="w-3 h-3 text-black" />}
                      </div>
                    </div>
                  </div>
                </div>
                )}

                {/* Promo Code Section - Inside order summary */}
                <div className="border-t border-zinc-700 pt-3">
                  {hasOfferItems ? (
                    <div className="bg-amber-500/10 border border-amber-500/30 p-2">
                      <p className="font-mono text-[10px] text-amber-400">
                        CODES CANNOT BE USED WITH OFFER ITEMS
                      </p>
                    </div>
                  ) : appliedCode ? (
                    <div className={`flex items-center justify-between gap-2 ${appliedCode.is_auto ? 'bg-purple-500/10 border border-purple-500/30' : 'bg-green-500/10 border border-green-500/30'} p-2`}>
                      <div className="flex items-center gap-2 min-w-0">
                        <Check className={`w-3 h-3 ${appliedCode.is_auto ? 'text-purple-500' : 'text-green-500'} shrink-0`} />
                        <span className={`font-mono text-xs ${appliedCode.is_auto ? 'text-purple-400' : 'text-green-400'} truncate`}>
                          {appliedCode.message || `${appliedCode.code}: -$${discountAmount.toFixed(2)}`}
                        </span>
                      </div>
                      {!appliedCode.is_auto && (
                        <button
                          type="button"
                          onClick={removeAppliedCode}
                          className="text-zinc-500 hover:text-red-500 transition-colors shrink-0"
                          data-testid="remove-promo-code"
                        >
                          <XCircle className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  ) : (
                    <div className="flex gap-2">
                      <Input
                        type="text"
                        value={promoCode}
                        onChange={(e) => {
                          setPromoCode(e.target.value.toUpperCase());
                          setCodeError(null);
                        }}
                        placeholder="PROMO CODE"
                        className={`flex-1 min-w-0 h-9 bg-zinc-800 border-zinc-700 text-white font-mono text-xs placeholder:text-zinc-600 focus:border-tiffany-500 rounded-none ${codeError ? 'border-red-500' : ''}`}
                        disabled={validatingCode}
                        data-testid="promo-code-input"
                      />
                      <Button
                        type="button"
                        onClick={validatePromoCode}
                        disabled={!promoCode.trim() || validatingCode}
                        className="h-9 bg-zinc-700 text-white hover:bg-zinc-600 font-mono text-xs px-3 rounded-none disabled:opacity-50 shrink-0"
                        data-testid="apply-promo-code"
                      >
                        {validatingCode ? (
                          <Loader2 className="w-3 h-3 animate-spin" />
                        ) : (
                          'APPLY'
                        )}
                      </Button>
                    </div>
                  )}
                  {codeError && (
                    <p className="font-mono text-[10px] text-red-400 mt-1">{codeError}</p>
                  )}
                </div>

                {/* Gift Card Section - Stacks with other discounts */}
                <div className="border-t border-zinc-700 pt-3">
                  {appliedGiftCard ? (
                    <div className="flex items-center justify-between gap-2 bg-tiffany-500/10 border border-tiffany-500/30 p-2">
                      <div className="flex items-center gap-2 min-w-0">
                        <CreditCard className="w-3 h-3 text-tiffany-500 shrink-0" />
                        <div className="min-w-0">
                          <span className="font-mono text-xs text-tiffany-400 block truncate">
                            GIFT CARD: -${giftCardAmount.toFixed(2)}
                          </span>
                          <span className="font-mono text-[10px] text-zinc-500 block">
                            {appliedGiftCard.remaining_after_use > 0 
                              ? `REMAINING BALANCE: $${appliedGiftCard.remaining_after_use.toFixed(2)}` 
                              : 'FULL BALANCE APPLIED'}
                          </span>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={removeGiftCard}
                        className="text-zinc-500 hover:text-red-500 transition-colors shrink-0"
                        data-testid="remove-gift-card"
                      >
                        <XCircle className="w-4 h-4" />
                      </button>
                    </div>
                  ) : (
                    <div>
                      <div className="flex gap-2">
                        <Input
                          type="text"
                          value={giftCardCode}
                          onChange={(e) => {
                            setGiftCardCode(e.target.value.toUpperCase());
                            setGiftCardError(null);
                          }}
                          placeholder="GIFT CARD CODE"
                          className={`flex-1 min-w-0 h-9 bg-zinc-800 border-zinc-700 text-white font-mono text-xs placeholder:text-zinc-600 focus:border-tiffany-500 rounded-none ${giftCardError ? 'border-red-500' : ''}`}
                          disabled={validatingGiftCard}
                          data-testid="gift-card-input"
                        />
                        <Button
                          type="button"
                          onClick={validateGiftCard}
                          disabled={!giftCardCode.trim() || validatingGiftCard}
                          className="h-9 bg-zinc-700 text-white hover:bg-zinc-600 font-mono text-xs px-3 rounded-none disabled:opacity-50 shrink-0"
                          data-testid="apply-gift-card"
                        >
                          {validatingGiftCard ? (
                            <Loader2 className="w-3 h-3 animate-spin" />
                          ) : (
                            'APPLY'
                          )}
                        </Button>
                      </div>
                      {giftCardError && (
                        <p className="font-mono text-[10px] text-red-400 mt-1">{giftCardError}</p>
                      )}
                    </div>
                  )}
                </div>

                {/* Totals */}
                <div className="border-t border-zinc-700 pt-3 space-y-1">
                  <div className="flex justify-between">
                    <span className="font-mono text-xs text-zinc-500">SUBTOTAL</span>
                    <span className="font-mono text-sm text-white">${cart.total.toFixed(2)}</span>
                  </div>
                  {appliedCode && discountAmount > 0 && (
                    <div className="flex justify-between">
                      <span className={`font-mono text-xs ${appliedCode.is_auto ? 'text-purple-500' : 'text-green-500'}`}>
                        {appliedCode.is_auto ? 'BLKCRD - 15% OFF' : `DISCOUNT (${appliedCode.code})`}
                      </span>
                      <span className={`font-mono text-sm ${appliedCode.is_auto ? 'text-purple-500' : 'text-green-500'}`}>-${discountAmount.toFixed(2)}</span>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span className="font-mono text-xs text-zinc-500">SHIPPING</span>
                    <span className={`font-mono text-sm ${
                      isDigitalOnly ? 'text-zinc-500'
                      : effectiveOvernightShipping ? 'text-tiffany-500'
                      : effectivePriorityShipping ? 'text-tiffany-500'
                      : 'text-green-500'
                    }`}>
                      {isDigitalOnly
                        ? 'N/A'
                        : effectiveOvernightShipping ? '$50.00'
                        : effectivePriorityShipping ? '$15.00'
                        : 'FREE'}
                    </span>
                  </div>
                  {appliedGiftCard && giftCardAmount > 0 && (
                    <div className="flex justify-between">
                      <span className="font-mono text-xs text-tiffany-500">GIFT CARD</span>
                      <span className="font-mono text-sm text-tiffany-500">-${giftCardAmount.toFixed(2)}</span>
                    </div>
                  )}
                  <div className="flex justify-between pt-2 border-t border-zinc-800">
                    <span className="font-sans text-lg text-white">TOTAL</span>
                    <span className="font-mono text-xl text-tiffany-500" data-testid="checkout-total">
                      ${total.toFixed(2)}
                    </span>
                  </div>
                </div>
                
                {/* Important Payment Instructions Box - Compact */}
                <div className="border border-amber-500/40 bg-amber-500/5 p-3 flex items-center justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <AlertTriangle className="w-4 h-4 text-amber-500 flex-shrink-0" />
                    <p className="font-mono text-xs text-zinc-300">
                      <span className="text-amber-500 font-bold">PayPal F&F</span> to <span className="text-tiffany-500">payments@shopblkmrkt.com</span> within 30min • <span className="text-red-400">NO G&S</span>
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      navigator.clipboard.writeText('payments@shopblkmrkt.com');
                      toast.success('PayPal email copied!');
                    }}
                    className="flex items-center gap-1 px-2 py-1 bg-zinc-800 hover:bg-zinc-700 text-tiffany-500 font-mono text-xs transition-colors"
                    data-testid="copy-paypal-checkout"
                  >
                    <Copy className="w-3 h-3" />
                    COPY
                  </button>
                </div>
                
                {/* Place Order Button */}
                <Button
                  type="submit"
                  disabled={submitting}
                  className="w-full h-14 bg-tiffany-500 text-black hover:bg-tiffany-400 font-sans text-xl tracking-widest rounded-none disabled:opacity-50 transition-all"
                  data-testid="place-order-btn"
                >
                  {submitting ? 'PROCESSING...' : 'PLACE ORDER'}
                </Button>
                
                <p className="font-mono text-[10px] text-zinc-600 text-center">
                  By placing this order, you agree to send payment via PayPal F&F within 30 minutes
                </p>
              </div>
            </form>
          </>
        )}
      </main>
    </div>
  );
}
